import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  History,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  ShieldCheck,
  UsersRound,
  Globe2,
  Sparkles,
  CalendarDays,
  Trash2,
  MessageCircle,
  FileText,
  MapPin,
  MapPinned,
  Pencil,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Gauge,
  AlertTriangle,
  Database,
  CreditCard,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import GlobalJobsBanner from "@/components/GlobalJobsBanner";
import LocationBar from "@/components/LocationBar";
import BillingBanner from "@/components/billing/BillingBanner";
import ActivationWall from "@/components/billing/ActivationWall";

// Single source of truth for sidebar nav order. Order here IS the default
// arrangement any user sees before they drag-reorder tabs. Admin-only entries
// are hidden for standard members but the relative order is preserved.
const ALL_NAV = [
  { to: "/admin-dashboard", label: "Admin Dashboard", icon: Gauge, testid: "nav-admin-dashboard", adminOnly: true },
  { to: "/admin-errors", label: "Errors", icon: AlertTriangle, testid: "nav-admin-errors", adminOnly: true },
  { to: "/admin-backups", label: "Backup & Sync", icon: Database, testid: "nav-admin-backups", adminOnly: true },
  { to: "/locations", label: "Locations", icon: MapPin, testid: "nav-locations", adminOnly: true },
  { to: "/users", label: "Users", icon: UsersRound, testid: "nav-users", adminOnly: true },
  { to: "/admin-billing", label: "Billing Information", icon: Receipt, testid: "nav-admin-billing", adminOnly: true },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/open-houses", label: "Open Houses", icon: CalendarDays, testid: "nav-open-houses" },
  { to: "/territory", label: "Territory Import", icon: MapPinned, testid: "nav-territory", territoryGated: true },
  { to: "/import-realtor-ca", label: "Step 1: Import (Realtor.ca)", icon: Globe2, testid: "nav-import-realtor-ca" },
  { to: "/realtors", label: "Step 2: Realtors", icon: Users, testid: "nav-realtors" },
  { to: "/brokerages", label: "Step 3: Brokerages", icon: Building2, testid: "nav-brokerages" },
  { to: "/outreach", label: "Step 4: Outreach", icon: MessageCircle, testid: "nav-outreach" },
  { to: "/templates", label: "Templates", icon: FileText, testid: "nav-templates" },
  { to: "/cleanup", label: "Cleanup", icon: Sparkles, testid: "nav-cleanup" },
  { to: "/scrape-history", label: "Scrape History", icon: History, testid: "nav-scrape-history" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/billing", label: "Billing & Credits", icon: CreditCard, testid: "nav-billing" },
  { to: "/recycle-bin", label: "Recycle Bin", icon: Trash2, testid: "nav-recycle-bin" },
];

function orderNavItems(items, savedOrder) {
  if (!savedOrder || !savedOrder.length) return items;
  const idx = new Map(savedOrder.map((path, i) => [path, i]));
  // Items added after the user saved an order slot in right after their default-order neighbour
  // (not at the very end), so new tabs land where everyone else sees them.
  const pos = (item) => {
    if (idx.has(item.to)) return idx.get(item.to);
    const i = items.indexOf(item);
    for (let k = i - 1; k >= 0; k--) {
      if (idx.has(items[k].to)) return idx.get(items[k].to) + (i - k) / 100;
    }
    return -1 + i / 100;
  };
  return [...items].sort((a, b) => pos(a) - pos(b));
}

/**
 * Poll the /cleanup/dirty-count endpoint so we can render a red badge on the
 * "Cleanup" nav item whenever any dirty-data item (garbage rows, phone/name
 * dupes, name-fix suggestions, manual outliers, brokerage-normalization
 * groups) is detected. Admin-only — otherwise the endpoint 403s. Polls every
 * 60s AND refreshes when the user lands on /cleanup so newly-merged records
 * shrink the badge without waiting for the next poll tick.
 */
function useCleanupDirtyCount(enabled) {
  const [count, setCount] = useState(0);
  const location = useLocation();
  const lastFetch = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/cleanup/dirty-count");
        if (!cancelled) setCount(Number(data?.total) || 0);
        lastFetch.current = Date.now();
      } catch (_) {
        // silently ignore — badge just stays at its last known value
      }
    };
    fetchCount();
    // Poll every 60s. Also on tab-visibility resume so a returning admin gets
    // fresh counts without waiting.
    const interval = setInterval(fetchCount, 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible" && Date.now() - lastFetch.current > 5_000) {
        fetchCount();
      }
    };
    // Fired by the Cleanup page after every audit run / merge so the nav
    // badge shrinks the instant work happens — no 60s wait.
    const onAuditUpdated = () => fetchCount();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("cleanup-audit-updated", onAuditUpdated);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("cleanup-audit-updated", onAuditUpdated);
    };
  }, [enabled]);

  // Refresh when the user navigates into the Cleanup page — likely to have
  // just applied a merge and we want the badge to drop immediately.
  useEffect(() => {
    if (!enabled) return;
    if (location.pathname !== "/cleanup") return;
    (async () => {
      try {
        const { data } = await api.get("/cleanup/dirty-count");
        setCount(Number(data?.total) || 0);
        lastFetch.current = Date.now();
      } catch (_) { /* noop */ }
    })();
  }, [location.pathname, enabled]);

  return count;
}


/**
 * Poll the admin errors "recent-count" endpoint so the sidebar can show a
 * red badge next to the Errors nav item when new client crashes / backend
 * 5xx / integration failures arrive. The count auto-clears once the admin
 * opens the /admin-errors page (which POSTs mark-seen).
 */
function useErrorBadgeCount(enabled) {
  const [count, setCount] = useState(0);
  const location = useLocation();
  const lastFetch = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/app-errors/recent-count?hours=24");
        if (!cancelled) setCount(Number(data?.count) || 0);
        lastFetch.current = Date.now();
      } catch (_) { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible" && Date.now() - lastFetch.current > 5_000) {
        fetchCount();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled]);

  // The Errors tab itself POSTs mark-seen on mount → any lingering count is
  // stale. Zero it locally when the admin lands on the page.
  useEffect(() => {
    if (!enabled) return;
    if (location.pathname !== "/admin-errors") return;
    setCount(0);
  }, [location.pathname, enabled]);

  return count;
}


/**
 * Poll the /realtors/review-summary endpoint so the sidebar can show a badge
 * next to the "Step 2: Realtors" nav item whenever open-house scans have
 * queued realtors awaiting approval — mirrors the in-page "Review" sub-tab
 * count. Refreshes on a 60s tick, on tab-visibility resume, when the user
 * lands on /realtors, and when the page fires `realtors-review-updated`
 * after a bulk approve/reject so the badge shrinks instantly.
 */
function useReviewCount(enabled) {
  const [count, setCount] = useState(0);
  const location = useLocation();
  const lastFetch = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/realtors/review-summary");
        if (!cancelled) setCount(Number(data?.pending) || 0);
        lastFetch.current = Date.now();
      } catch (_) { /* silent — badge keeps its last value */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible" && Date.now() - lastFetch.current > 5_000) {
        fetchCount();
      }
    };
    const onReviewUpdated = () => fetchCount();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("realtors-review-updated", onReviewUpdated);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("realtors-review-updated", onReviewUpdated);
    };
  }, [enabled]);

  // Refresh when landing on the Realtors page (likely to have just approved
  // or rejected some) so the badge reflects reality without the 60s wait.
  useEffect(() => {
    if (!enabled) return;
    if (location.pathname !== "/realtors") return;
    (async () => {
      try {
        const { data } = await api.get("/realtors/review-summary");
        setCount(Number(data?.pending) || 0);
        lastFetch.current = Date.now();
      } catch (_) { /* noop */ }
    })();
  }, [location.pathname, enabled]);

  return count;
}


/**
 * Sidebar badges for the Outreach nav item:
 *   1. cold (red)  — referral partners not contacted in >threshold days
 *   2. queue (yellow) — remaining daily follow-queue count
 * Cold count is polled from the backend every 60s. Queue remaining is read
 * from localStorage (same keys the Outreach page writes) and updated in
 * real-time via a custom event the Outreach page dispatches.
 */
function useOutreachBadges(enabled) {
  const [cold, setCold] = useState(0);
  const [queueRemaining, setQueueRemaining] = useState(null);
  const location = useLocation();
  const lastFetch = useRef(0);

  // Read queue remaining from localStorage (same keys the Outreach page uses).
  const readQueueFromStorage = () => {
    try {
      const target = Number(localStorage.getItem("outreach.queueDailyTarget")) || 20;
      const raw = localStorage.getItem("outreach.queueDailyDone");
      if (!raw) return target;
      const done = JSON.parse(raw);
      const today = new Date().toISOString().slice(0, 10);
      const count = done.date === today ? (done.count || 0) : 0;
      return Math.max(0, target - count);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const fetchCold = async () => {
      try {
        const { data } = await api.get("/outreach/sidebar-badges");
        if (!cancelled) setCold(Number(data?.cold_count) || 0);
        lastFetch.current = Date.now();
      } catch (_) { /* silent */ }
    };
    fetchCold();
    const interval = setInterval(fetchCold, 60_000);

    // Read initial queue from localStorage
    setQueueRemaining(readQueueFromStorage());

    // Listen for the Outreach page broadcasting remaining-count changes
    const onQueueUpdate = (e) => {
      const remaining = e.detail?.remaining;
      if (typeof remaining === "number") setQueueRemaining(remaining);
    };
    window.addEventListener("outreach-queue-update", onQueueUpdate);

    // Also re-read from storage on visibility resume (handles another tab)
    const onVis = () => {
      if (document.visibilityState === "visible") {
        if (Date.now() - lastFetch.current > 5_000) fetchCold();
        setQueueRemaining(readQueueFromStorage());
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("outreach-queue-update", onQueueUpdate);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled]);

  // Refresh cold count when the user lands on the Outreach page.
  useEffect(() => {
    if (!enabled || location.pathname !== "/outreach") return;
    (async () => {
      try {
        const { data } = await api.get("/outreach/sidebar-badges");
        setCold(Number(data?.cold_count) || 0);
        lastFetch.current = Date.now();
      } catch (_) { /* noop */ }
    })();
    setQueueRemaining(readQueueFromStorage());
  }, [location.pathname, enabled]);

  return { cold, queueRemaining };
}


function NavLinks({ onClick, mobile = false, isAdmin = false }) {
  const { user, refreshMe } = useAuth();
  const [reordering, setReordering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  // Filter admin-only nav entries out for standard members, keeping the
  // relative order defined in ALL_NAV as the default.
  const base = ALL_NAV.filter((n) => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.territoryGated && !(isAdmin || user?.territory_access)) return false;
    return true;
  });
  const items = orderNavItems(base, user?.nav_order);

  // Cleanup badge — polls the lightweight /cleanup/dirty-count endpoint.
  // Both admins and members can access Cleanup now (scoped to their
  // location_ids for members), so both need the badge poll.
  const cleanupDirtyCount = useCleanupDirtyCount(true);
  // Errors badge — admin-only endpoint, so poll only for admins.
  const errorCount = useErrorBadgeCount(isAdmin);
  // Review badge — realtors awaiting approval from open-house scans. Scoped
  // to the caller's locations server-side, so poll for everyone.
  const reviewCount = useReviewCount(true);
  // Outreach badges — red (cold referrals) + yellow (queue remaining).
  const outreachBadges = useOutreachBadges(true);

  const saveOrder = async (order) => {
    setSaving(true);
    try {
      await api.put("/me/profile", { nav_order: order });
      await refreshMe();
    } catch (e) {
      toast.error("Couldn't save tab order", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length || saving) return;
    const order = items.map((i) => i.to);
    [order[index], order[target]] = [order[target], order[index]];
    saveOrder(order);
  };

  const dropAt = (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex || saving) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const order = items.map((i) => i.to);
    const [moved] = order.splice(dragIndex, 1);
    order.splice(targetIndex, 0, moved);
    setDragIndex(null);
    setOverIndex(null);
    saveOrder(order);
  };

  return (
    <nav className="flex flex-col gap-1 px-3 py-2">
      <div className="flex items-center justify-between px-3 pb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
          Menu
        </span>
        <button
          type="button"
          onClick={() => setReordering((v) => !v)}
          className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
            reordering
              ? "bg-slate-900 text-white"
              : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          }`}
          title="Re-order tabs"
          data-testid={mobile ? "nav-reorder-toggle-mobile" : "nav-reorder-toggle"}
        >
          <Pencil className="h-3 w-3" />
          {reordering ? "Done" : "Reorder"}
        </button>
      </div>
      {reordering && !mobile ? (
        <p className="px-3 pb-1 text-[10px] text-slate-400">Drag tabs into place</p>
      ) : null}
      {items.map(({ to, label, icon: Icon, testid }, index) => (
        <div
          key={to}
          className={`flex items-center gap-1 rounded-md transition-colors ${
            reordering && !mobile ? "cursor-grab active:cursor-grabbing" : ""
          } ${overIndex === index && dragIndex !== null && dragIndex !== index ? "ring-2 ring-[hsl(var(--brand))] ring-offset-1" : ""} ${
            dragIndex === index ? "opacity-40" : ""
          }`}
          draggable={reordering && !mobile}
          onDragStart={(e) => {
            if (!reordering || mobile) return;
            e.dataTransfer.effectAllowed = "move";
            setDragIndex(index);
          }}
          onDragOver={(e) => {
            if (!reordering || mobile || dragIndex === null) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (overIndex !== index) setOverIndex(index);
          }}
          onDrop={(e) => {
            if (!reordering || mobile) return;
            e.preventDefault();
            dropAt(index);
          }}
          onDragEnd={() => {
            setDragIndex(null);
            setOverIndex(null);
          }}
          data-testid={`nav-row-${testid}${mobile ? "-mobile" : ""}`}
        >
          {reordering && !mobile ? (
            <GripVertical className="h-4 w-4 shrink-0 text-slate-300 ml-1" aria-hidden="true" />
          ) : null}
          <NavLink
            to={to}
            end={to === "/"}
            onClick={(e) => {
              if (reordering) {
                e.preventDefault();
                return;
              }
              onClick && onClick();
            }}
            data-testid={mobile ? `${testid}-mobile` : testid}
            className={({ isActive }) =>
              [
                "flex flex-1 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                "min-h-[44px] min-w-0",
                reordering ? "pointer-events-none select-none" : "",
                isActive && !reordering
                  ? "bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-strong))]"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              ].join(" ")
            }
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{label}</span>
            {to === "/cleanup" && cleanupDirtyCount > 0 ? (
              <span
                className="ml-auto inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold leading-none min-w-[18px] h-[18px] px-1 shrink-0"
                data-testid={mobile ? "nav-cleanup-badge-mobile" : "nav-cleanup-badge"}
                title={`${cleanupDirtyCount} dirty-data item${cleanupDirtyCount === 1 ? "" : "s"} to review`}
                aria-label={`${cleanupDirtyCount} dirty-data items to review`}
              >
                {cleanupDirtyCount > 99 ? "99+" : cleanupDirtyCount}
              </span>
            ) : null}
            {to === "/admin-errors" && errorCount > 0 ? (
              <span
                className="ml-auto inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold leading-none min-w-[18px] h-[18px] px-1 shrink-0"
                data-testid={mobile ? "nav-errors-badge-mobile" : "nav-errors-badge"}
                title={`${errorCount} new error${errorCount === 1 ? "" : "s"} since last visit`}
                aria-label={`${errorCount} new errors since last visit`}
              >
                {errorCount > 99 ? "99+" : errorCount}
              </span>
            ) : null}
            {to === "/realtors" && reviewCount > 0 ? (
              <span
                className="ml-auto inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold leading-none min-w-[18px] h-[18px] px-1 shrink-0"
                data-testid={mobile ? "nav-realtors-review-badge-mobile" : "nav-realtors-review-badge"}
                title={`${reviewCount} realtor${reviewCount === 1 ? "" : "s"} to review`}
                aria-label={`${reviewCount} realtors to review`}
              >
                {reviewCount > 99 ? "99+" : reviewCount}
              </span>
            ) : null}
            {to === "/outreach" && (outreachBadges.cold > 0 || (outreachBadges.queueRemaining ?? 0) > 0) ? (
              <span className="ml-auto inline-flex items-center gap-1 shrink-0">
                {outreachBadges.cold > 0 && (
                  <span
                    className="inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold leading-none min-w-[18px] h-[18px] px-1"
                    data-testid={mobile ? "nav-outreach-cold-badge-mobile" : "nav-outreach-cold-badge"}
                    title={`${outreachBadges.cold} cold referral partner${outreachBadges.cold === 1 ? "" : "s"}`}
                    aria-label={`${outreachBadges.cold} cold referral partners`}
                  >
                    {outreachBadges.cold > 99 ? "99+" : outreachBadges.cold}
                  </span>
                )}
                {(outreachBadges.queueRemaining ?? 0) > 0 && (
                  <span
                    className="inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-semibold leading-none min-w-[18px] h-[18px] px-1"
                    data-testid={mobile ? "nav-outreach-queue-badge-mobile" : "nav-outreach-queue-badge"}
                    title={`${outreachBadges.queueRemaining} left in today's follow queue`}
                    aria-label={`${outreachBadges.queueRemaining} queue remaining`}
                  >
                    {outreachBadges.queueRemaining > 99 ? "99+" : outreachBadges.queueRemaining}
                  </span>
                )}
              </span>
            ) : null}
          </NavLink>
          {reordering && mobile ? (
            <div className="flex flex-col shrink-0">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0 || saving}
                className="rounded p-0.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-25"
                title="Move up"
                data-testid={`nav-move-up-${testid}-mobile`}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === items.length - 1 || saving}
                className="rounded p-0.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-25"
                title="Move down"
                data-testid={`nav-move-down-${testid}-mobile`}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 px-5 py-5 border-b border-slate-200">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-md text-white font-bold tracking-tight"
        style={{ background: "hsl(var(--brand))" }}
        aria-hidden="true"
      >
        RC
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-semibold text-slate-900 font-display">Realtor Outreach CRM</span>
        <span className="text-[11px] uppercase tracking-wider text-slate-500">
          Brokerage &amp; agent network
        </span>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  // Wall: billing live + member blocked (never activated or subscription ended) → only Billing & Settings stay reachable.
  const walled = !isAdmin && !!user?.billing?.enabled && !!user.billing.blocked
    && !pathname.startsWith("/billing") && pathname !== "/settings";
  const limited = !isAdmin && !!user?.billing?.enabled && !!user?.billing?.blocked;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar — pinned to the viewport so the Sign-out button
          stays visible on long pages (Realtors, Open Houses agenda, etc.).
          h-screen + sticky top-0 makes the whole panel exactly one
          viewport tall; the nav scrolls internally when it overflows. */}
      <aside
        className="hidden md:flex md:flex-col md:w-64 md:h-screen md:sticky md:top-0 md:border-r md:border-slate-200 md:bg-white md:shrink-0"
        data-testid="desktop-sidebar"
      >
        <Brand />
        <div className="flex-1 overflow-y-auto py-2">
          <NavLinks isAdmin={isAdmin} />
        </div>
        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-2 text-xs text-slate-500 px-2 pb-2">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="truncate" data-testid="sidebar-user-email">
              {user?.email}
            </span>
            <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600 capitalize">
              {user?.role}
            </span>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2 min-h-[44px]"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile header + drawer */}
      <div className="flex flex-1 flex-col min-w-0">
        <header
          className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2 min-h-[56px]"
          data-testid="mobile-header"
        >
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="mobile-nav-toggle"
                  aria-label="Open navigation"
                  className="min-h-[44px] min-w-[44px]"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col">
                <Brand />
                <div className="flex-1 overflow-y-auto py-2 min-h-0">
                  <NavLinks onClick={() => setMobileOpen(false)} mobile isAdmin={isAdmin} />
                </div>
                <div className="px-3 pb-4 pt-2 border-t border-slate-200 shrink-0 bg-white">
                  <div className="text-xs text-slate-500 mb-2 truncate">{user?.email}</div>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 min-h-[44px]"
                    onClick={() => {
                      setMobileOpen(false);
                      handleLogout();
                    }}
                    data-testid="logout-button-mobile"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <div
              className="flex h-8 w-8 items-center justify-center rounded text-white text-sm font-bold"
              style={{ background: "hsl(var(--brand))" }}
              aria-hidden="true"
            >
              RC
            </div>
            <span className="text-sm font-semibold text-slate-900">Realtor Outreach CRM</span>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            {user?.role}
          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-x-clip" data-testid="main-content">
          {!limited ? <LocationBar /> : null}
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-7 lg:px-8 lg:py-9 space-y-5">
            <GlobalJobsBanner />
            <BillingBanner />
            {walled ? <ActivationWall billing={user.billing} /> : children}
          </div>
        </main>
      </div>
    </div>
  );
}
